/*---------------------------------------------------------------------------------------------
 *  Copyright (c)Cleartrip. All rights reserved.
 * 
 *  This Component is a Generic HOC for rendering Form fields, following the Benefit UI Config json data structure
 *  The Component has all the functionalities for updating fields, and updating the original JSON in place by reference
 *  The Component provides callbacks, boolean values that can be controlled by the parent page implementing this, to handle various states
*--------------------------------------------------------------------------------------------*/

import { useRef, useState } from "react";

import { Fields } from "../Fields";

export const RenderFormFieldsHOC = ({
    config, //This is a ref to the UI config json, which will get updated from this HOC
    parent, //On initial render this is the whole config json, on subsequent renders, its the nested recursive parent
    childIndex,//starts with 0 for initial render, on subsequent children it is passed the children index
    sectionId, //its an unique id sent by the UI config to give a id to a section element
    formSubmitClick, //this is a boolean to check if the submit button, for the form is clicked-> used to run validations
    errorCallback, //this is a callback to execute if any errors have occured in any of the form fields
    rerenderFormUI, //Pass the setState callback inside the parent Page using this component. Used to do any re-render of whole form: applicable in case of you are
    //changing the initial structure of the form Fields, Example in case of MultiChildren you have to add empty Children, which changes the initial form skeleton
    viewMode,//Boolean to control if the Form is in Edit mode or View Mode
    behaviorConfigCallback = () => { } // Callback fired for behaviorconfig: Note Currently one behaviorconfig per form 
    // is supported from UI to keep it extensible, It can't be multiple behavior configs
}) => {
    console.log("config passed:", config)

    //store the copy of the initial config ref value sent from parent
    const configRef = useRef({ ...(config) })
    const [collapsedRows, setCollapsedRows] = useState([]);
    const [selectedPaymentType, setSelectedPaymentType] = useState(0); // Default to first payment type
    const RenderMultiChildrenFields = ({ multiChildren, parent, sectionId, formSubmitClick, viewMode, rerenderFormUI: localRerenderFormUI }) => {
        // Add state to track collapsed rows
        
        
        // Initialize collapsedRows for benefit_section
        if (sectionId === "benefit_section" && collapsedRows.length === 0) {
            setCollapsedRows(new Array(multiChildren?.length || 0).fill(true));
        }
        // Keep copy of empty row, used to add blank child
        let emptyRow = [] //fallback case
        if (multiChildren && multiChildren.length) {
            emptyRow = multiChildren[0]?.map((child) => {
                // If the child has valueConfig with selectedValues, preserve them
                if (child?.valueConfig?.selectedValues) {
                    return {
                        ...child,
                        valueConfig: {
                            ...(child?.valueConfig),
                            selectedValues: child.valueConfig.selectedValues
                        }
                    };
                }
                // If the child has valueConfig but no selectedValues, set appropriate default
                else if (child?.valueConfig) {
                    return {
                        ...child,
                        valueConfig: {
                            ...(child?.valueConfig),
                            selectedValues: child?.valueConfig?.possibleValues ? [] : null
                        }
                    };
                }
                // If no valueConfig, return as is
                return child;
            });
        }

        const toggleRow = (index) => {
            if (sectionId !== "benefit_section") return;
            setCollapsedRows(prev => {
                const newState = [...prev];
                newState[index] = !newState?.[index];
                return newState;
            });
        };

        return (
            <>
                {
                    multiChildren?.map((children, index) => {
                        return (
                            <div className="flex items-center gap-8" key={index}>
                                <div className="flex flex-col w-full">
                                    {/* Row header with collapse/expand button - only show for benefit_section */}
                                    {sectionId === "benefit_section" && (
                                        <div className="flex items-center gap-2 mb-2">
                                            <button 
                                                onClick={() => toggleRow(index)}
                                                className="btn btn-sm btn-ghost p-1"
                                            >
                                                <svg 
                                                    className={`w-4 h-4 transition-transform ${!collapsedRows?.[index] ? 'rotate-90' : ''}`} 
                                                    fill="none" 
                                                    stroke="currentColor" 
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                            <span className="text-sm font-medium">Hurdle {index + 1}</span>
                                        </div>
                                    )}
                                    {console.log("collapsedRows: ", collapsedRows)}
                                    {/* Row content */}
                                    <div className={`items-center gap-2 ${sectionId === "benefit_section" && collapsedRows?.[index] ? 'hidden' : 'flex flex-wrap'}`}>
                                        {
                                            children.map((child, childIndex) => {
                                                const { fieldType, valueConfig, operators = null, displayAttributes, elementId, keyName, componentType } = child
                                                if (componentType && componentType === "ELEMENT") {
                                                    return (
                                                        <Fields
                                                            fieldType={fieldType}
                                                            displayAttributes={displayAttributes}
                                                            valueConfig={valueConfig}
                                                            operators={operators}
                                                            onChangeCallback={(updatedValue) => {
                                                                
                                                                //replace the current child with the updatedValue
                                                                children[childIndex] = {
                                                                    ...child,
                                                                    ...updatedValue
                                                                }
                                    
                                                            }}
                                                            onErrorCallback={(error) => errorCallback(error)}
                                                            elementId={elementId}
                                                            sectionId={sectionId}
                                                            formSubmitClick={formSubmitClick}
                                                            key={keyName}
                                                            viewMode={viewMode}
                                                        />
                                                    )
                                                }
                                                else if (componentType === "ELEMENT_GROUP" || componentType === "ELEMENT_CONDITION_GROUP" || componentType === "NESTED_ELEMENT") {
                                                    return RenderFormFields({
                                                        config: child,
                                                        parent: children,
                                                        childIndex,
                                                        sectionId,
                                                        formSubmitClick,
                                                        viewMode,
                                                        rerenderFormUI: localRerenderFormUI
                                                    })
                                                }
                                            })
                                        }
                                    </div>
                                </div>
                                
                                {/* Action buttons */}
                                {
                                    !viewMode &&
                                    <div className="flex gap-3 h-max items-center mt-7">
                                        {/* Don't allow user to remove all row, at least one row to be there */}
                                        {
                                            multiChildren.length > 1 &&
                                            <p className="font-medium text-red-400 cursor-pointer" onClick={() => {
                                                //remove the current index child
                                                const newMultiChildren = multiChildren.filter((_children, _index) => _index !== index)
                                                parent.multiChildren = newMultiChildren
                                                if (localRerenderFormUI) {
                                                    localRerenderFormUI(prev => ({
                                                        ...prev,
                                                        catalogComponent: { ...(configRef.current) }
                                                    }))
                                                }
                                            }}>Remove</p>
                                        }
                                        {
                                            // Show add button only in the last child 
                                            index === multiChildren.length - 1 ?
                                                <p className="font-medium text-primary cursor-pointer" onClick={() => {
                                                    //add the same row child as copy in the parent multiChildren and trigger a re-render
                                                    parent.multiChildren.push(emptyRow)
                                                    // When adding a new row, collapse all previous rows - only for benefit_section
                                                        if (sectionId === "benefit_section") {
                                                        setCollapsedRows(prev => prev?.map(() => true).concat(false));
                                                    }
                                                    if (localRerenderFormUI) {
                                                        localRerenderFormUI(prev => ({
                                                            ...prev,
                                                            catalogComponent: { ...(configRef.current) }
                                                        }))
                                                    }
                                                  
                                                }}>Add</p>
                                                : null
                                        }
                                    </div>
                                }
                            </div>
                        )
                    })
                }
            </>
        )
    }


    const RenderFormFields = ({ config, parent, childIndex, sectionId, formSubmitClick, viewMode, parentConfig, rerenderFormUI: localRerenderFormUI }) => {

        // Helper function to clear selectedValues from all children recursively
        const clearChildrenSelectedValues = (node) => {
            if (!node) return
            
            // Clear selectedValues from current node if it has valueConfig
            if (node.valueConfig) {
                if (node.valueConfig.selectedValues !== undefined && node.valueConfig.selectedValues !== null) {
                    // For all field types, clear to empty array to maintain consistent structure
                    // This ensures the field structure remains intact
                    node.valueConfig.selectedValues = []
                }
            }
            
            // Recursively clear children
            if (node.children && Array.isArray(node.children)) {
                node.children.forEach(child => {
                    clearChildrenSelectedValues(child)
                })
            }
            
            // Handle multiChildren (for MULTI_ADD componentSubtype)
            if (node.multiChildren && Array.isArray(node.multiChildren)) {
                node.multiChildren.forEach(multiChildArray => {
                    if (Array.isArray(multiChildArray)) {
                        multiChildArray.forEach(child => {
                            clearChildrenSelectedValues(child)
                        })
                    }
                })
            }
        }

        // Handle NESTED_ELEMENT with CHECKBOX fieldType
        if (config?.componentType === "NESTED_ELEMENT" && config?.fieldType === "CHECKBOX") {
            const { displayAttributes, valueConfig, children } = config
            const { displayName } = displayAttributes || {}
            
            // Get checked state from selectedValues, default to false
            const isChecked = valueConfig?.selectedValues?.includes("true") || 
                            valueConfig?.selectedValues?.[0] === "true" || 
                            false
            
            const handleCheckboxChange = (e) => {
                const checked = e.target.checked
                const newSelectedValues = checked ? ["true"] : ["false"]
                
                // Create a deep copy of the config to avoid mutating the original
                // Deep clone children array to ensure we're modifying a copy
                const deepCloneChildren = (children) => {
                    if (!children || !Array.isArray(children)) return children
                    return children.map(child => ({
                        ...child,
                        valueConfig: child.valueConfig ? { ...child.valueConfig } : undefined,
                        children: deepCloneChildren(child.children),
                        multiChildren: child.multiChildren ? child.multiChildren.map(mc => 
                            Array.isArray(mc) ? mc.map(c => ({
                                ...c,
                                valueConfig: c.valueConfig ? { ...c.valueConfig } : undefined
                            })) : mc
                        ) : undefined
                    }))
                }
                
                const updatedConfig = {
                    ...config,
                    valueConfig: {
                        ...valueConfig,
                        selectedValues: newSelectedValues
                    },
                    children: deepCloneChildren(config.children)
                }
                
                // If checkbox is being unchecked, clear all selectedValues from children
                if (!checked && updatedConfig.children && Array.isArray(updatedConfig.children)) {
                    updatedConfig.children.forEach(child => {
                        clearChildrenSelectedValues(child)
                    })
                }
                
                // Update the config with new selectedValues
                parent.children[childIndex] = updatedConfig
                
                // Trigger re-render if rerenderFormUI is available
                const rerenderCallback = localRerenderFormUI || rerenderFormUI
                if (rerenderCallback) {
                    rerenderCallback(prev => {
                        // Return updated state to trigger re-render
                        // The actual data is already updated in place via parent.children[childIndex]
                        return {
                            ...prev,
                            catalogComponent: prev?.catalogComponent || prev
                        }
                    })
                }
            }

            return (
                <div className="flex flex-col gap-2" id={config.keyName} key={config.keyName}>
                    <label className="cursor-pointer flex items-center">
                        <span className="label-text text-base mr-2">{displayName}</span>
                        <input 
                            type="checkbox" 
                            checked={isChecked}
                            onChange={handleCheckboxChange}
                            className="checkbox checkbox-primary"
                            disabled={viewMode || displayAttributes?.disabled}
                        />
                    </label>
                    
                    {/* Show children only when checkbox is checked */}
                    {isChecked && children && Array.isArray(children) && children.length > 0 && (
                        <div className="ml-6 flex flex-col gap-2 border-l-2 border-gray-300 pl-4">
                            {children.map((childConfig, index) => {
                                return RenderFormFields({
                                    config: childConfig,
                                    parent: config,
                                    childIndex: index,
                                    sectionId,
                                    formSubmitClick,
                                    viewMode,
                                    rerenderFormUI: localRerenderFormUI || rerenderFormUI
                                })
                            })}
                        </div>
                    )}
                </div>
            )
        }

        if (config?.componentType === "ELEMENT") {
            const { fieldType, valueConfig, operators = null, displayAttributes, elementId, behaviourConfig } = config
            return (
                <Fields
                    fieldType={fieldType}
                    displayAttributes={displayAttributes}
                    valueConfig={valueConfig}
                    operators={operators}
                    onChangeCallback={(updatedValue) => {

                        parent.children[childIndex] = {
                            ...config, //spread all other properties not exported from Field
                            ...updatedValue //spread rest properties updated by Field
                        }

                        if (behaviourConfig) {
                            behaviorConfigCallback({ updatedValue, behaviourConfig })
                        }
                    }}
                    onErrorCallback={(error) => errorCallback(error)}
                    elementId={elementId}
                    sectionId={sectionId} //used only for styling in dropdown, to get respective section clientHeight
                    formSubmitClick={formSubmitClick}
                    key={config.keyName}
                    viewMode={viewMode}
                    parentConfig={parentConfig}
                />
            )
        }

        //if component type is ELEMENT_GROUP: need to wrap form elements inside one container
        else if (config?.componentType && config?.componentType === "ELEMENT_GROUP") {
            const { displayName } = config.displayAttributes || {}
            // Check if this is a validity window group
            const isValidityWindow = config.keyName === "validity_window" || 
                                   (displayName && displayName.includes("Validity Window"));
            
            //traverse through the children and then recursively call the function
            if (config?.componentSubtype === "MULTI_ADD") {
                let isDateTimeGroup = false
                if (config && config.multiChildren
                    && (Array.isArray(config.multiChildren)
                        && config.multiChildren[0]?.fieldType === "DATE_PICKER"
                        || config.multiChildren[0].fieldType === "TIME_PICKER")) {

                    isDateTimeGroup = true
                }
                return (
                    // Use a different rendering mechanism for this group only 
                    <div className="flex flex-col gap-1 relative border-t-2 border-b-2 border-accent py-3" id={config.keyName} key={config.keyName}>
                        {displayName ? <p className="text-lg">{displayName}</p> : null}
                        <div className={`flex gap-1 ${!isDateTimeGroup ? "flex-col" : ""}`}>
                            {
                                RenderMultiChildrenFields({
                                    multiChildren: config.multiChildren,
                                    parent: config,
                                    sectionId,
                                    formSubmitClick,
                                    viewMode,
                                    rerenderFormUI: localRerenderFormUI || rerenderFormUI
                                })
                            }
                        </div>
                    </div>
                )

            } else {
                //Non Multi add elemenet groups
                let isDateTimeGroup = false
                if (config
                    && config.children[0].fieldType === "DATE_PICKER"
                    || config.children[0].fieldType === "TIME_PICKER") {

                    isDateTimeGroup = true
                }
                return (
                    <div className="flex flex-col gap-1 border-t-2 border-b-2 border-accent py-3" id={config.keyName} key={config.keyName}>
                        {displayName ? <p className="text-lg">{displayName}</p> : null}
                        <div className={`flex gap-1 ${!isDateTimeGroup ? "flex-col" : ""}`}>
                            {
                                //ELEMENT_GROUP will always have a children
                                config.children?.map((childConfig, index) => {
                                    // For date fields in validity window, pass parent config
                                    if (isValidityWindow && 
                                        (childConfig.fieldType === "DATE_PICKER" || childConfig.fieldType === "TIME_PICKER")) {
                                        return RenderFormFields({
                                            config: childConfig,
                                            parent: config,
                                            childIndex: index,
                                            sectionId,
                                            formSubmitClick,
                                            viewMode,
                                            // Pass parent config for date validation
                                            parentConfig: config,
                                            rerenderFormUI: localRerenderFormUI || rerenderFormUI
                                        })
                                    }
                                    
                                    // Regular rendering for other fields
                                    return RenderFormFields({
                                        config: childConfig,
                                        parent: config,
                                        childIndex: index,
                                        sectionId,
                                        formSubmitClick,
                                        viewMode,
                                        rerenderFormUI: localRerenderFormUI || rerenderFormUI
                                    })
                                })
                            }
                        </div>
                    </div>
                )
            }

        }
        //in case of componentType like section and page 
        else if (config?.children && config?.children?.length) {
            if (config.componentType === "PAGE") {
                return <div className="flex flex-col gap-2" id={config.catalogId}>
                    {
                        config.children?.map((childConfig, index) => {
                            return RenderFormFields({
                                config: childConfig,
                                parent: config,
                                childIndex: index,
                                sectionId,
                                formSubmitClick,
                                viewMode,
                                rerenderFormUI: localRerenderFormUI || rerenderFormUI
                            })
                        })
                    }
                </div>
            }
            else if (config?.componentType === "SECTION") {
                const { displayName } = config.displayAttributes
                
                // Helper function to get selected payment modes for payment_restrictions_section
                const getSelectedPaymentModes = (sectionConfig) => {
                    if (sectionConfig?.keyName !== "payment_restrictions_section") {
                        return []
                    }
                    
                    const selectedModes = []
                    
                    // Find payment_restrictions ELEMENT_CONDITION_GROUP
                    const paymentRestrictions = sectionConfig.children?.find(
                        child => child?.keyName === "payment_restrictions" && 
                                 child?.componentType === "ELEMENT_CONDITION_GROUP"
                    )
                    
                    if (!paymentRestrictions?.children) {
                        return selectedModes
                    }
                    
                    // Iterate through all payment type groups
                    paymentRestrictions.children.forEach(paymentTypeGroup => {
                        if (paymentTypeGroup?.componentType === "ELEMENT_CONDITION_GROUP" &&
                            paymentTypeGroup?.keyName?.startsWith("payment_type_")) {
                            
                            // Find the checkbox (NESTED_ELEMENT with CHECKBOX) in this payment type group
                            const checkbox = paymentTypeGroup.children?.find(
                                child => child?.componentType === "NESTED_ELEMENT" &&
                                         child?.fieldType === "CHECKBOX" &&
                                         (child?.elementId?.includes("enable-restriction") ||
                                          child?.keyName?.includes("check_box"))
                            )
                            
                            // Check if checkbox is enabled (selectedValues includes "true")
                            const isEnabled = checkbox?.valueConfig?.selectedValues?.includes("true") ||
                                             checkbox?.valueConfig?.selectedValues?.[0] === "true"
                            
                            if (isEnabled && paymentTypeGroup?.displayAttributes?.displayName) {
                                selectedModes.push(paymentTypeGroup.displayAttributes.displayName)
                            }
                        }
                    })
                    
                    return selectedModes
                }
                
                const selectedPaymentModes = getSelectedPaymentModes(config)
                
                return (

                    <div className="collapse overflow-visible bg-white border border-gray-500 shadow-md py-2" id={config.keyName} key={config.keyName}>
                        <input type="radio" name="my-accordion-1" defaultChecked={childIndex === 0} />
                        <div className="collapse-title text-xl font-medium">
                            {displayName}
                        </div>
                        <div className={`collapse-content flex flex-col ${config.keyName === "payment_restrictions_section" ? "gap-0" : "gap-2"}`}>
                            {/* Display selected payment modes for payment_restrictions_section */}
                            {config.keyName === "payment_restrictions_section" && selectedPaymentModes.length > 0 && (
                                <div className="mb-3 p-2 bg-gray-50 rounded-md">
                                    <p className="text-sm font-medium text-gray-700">
                                        Payment Modes Selected: {selectedPaymentModes.join(", ")}
                                    </p>
                                </div>
                            )}
                            {
                                config.children?.map((childConfig, index) => {
                                    return RenderFormFields({
                                        config: childConfig,
                                        parent: config,
                                        childIndex: index,
                                        sectionId: config.keyName,
                                        formSubmitClick,
                                        viewMode,
                                        rerenderFormUI: localRerenderFormUI || rerenderFormUI
                                    })
                                })
                            }
                        </div>
                    </div>
                )
            }
            else if (config?.componentType === "ELEMENT_CONDITION_GROUP") {
                const { displayName } = config.displayAttributes
                
                // Check if this is a payment restrictions parent (has ELEMENT_CONDITION_GROUP children)
                const hasPaymentTypeChildren = config.children?.some(
                    child => child?.componentType === "ELEMENT_CONDITION_GROUP"
                )
                
                // Helper function to check if payment restriction checkbox is enabled
                const isPaymentRestrictionEnabled = (paymentTypeConfig) => {
                    if (!paymentTypeConfig?.children || !Array.isArray(paymentTypeConfig.children)) {
                        return false
                    }
                    
                    // Look for NESTED_ELEMENT with CHECKBOX fieldType
                    for (const child of paymentTypeConfig.children) {
                        if (child?.componentType === "NESTED_ELEMENT" && 
                            child?.fieldType === "CHECKBOX" &&
                            child?.valueConfig?.selectedValues) {
                            const isChecked = child.valueConfig.selectedValues.includes("true") || 
                                            child.valueConfig.selectedValues[0] === "true"
                            if (isChecked) {
                                return true
                            }
                        }
                    }
                    return false
                }

                // If it's the payment restrictions parent, render two-panel layout
                if (hasPaymentTypeChildren && config.keyName === "payment_restrictions") {
                    const selectedIndex = selectedPaymentType
                    const selectedPaymentTypeConfig = config.children[selectedIndex]
                    
                    return (
                        <div className="flex flex-col" id={config.keyName} key={config.keyName}>
                            {displayName ? <p className="text-lg mb-2">{displayName}</p> : null}
                            <div className="flex gap-4">
                                {/* Left Panel: List of payment types */}
                                <div className="w-64 flex flex-col gap-2 border-r-2 border-gray-300 pr-4">
                                    {config.children?.map((childConfig, index) => {
                                        if (childConfig?.componentType === "ELEMENT_CONDITION_GROUP") {
                                            const isSelected = index === selectedIndex
                                            const isEnabled = isPaymentRestrictionEnabled(childConfig)
                                            return (
                                                <div
                                                    key={childConfig.keyName}
                                                    onClick={() => setSelectedPaymentType(index)}
                                                    className={`p-3 rounded-md cursor-pointer transition-colors ${
                                                        isSelected 
                                                            ? 'bg-primary text-white' 
                                                            : 'bg-gray-100 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            {isEnabled && (
                                                                <div className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500">
                                                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                    </svg>
                                                                </div>
                                                            )}
                                                            <span className="font-medium">
                                                                {childConfig.displayAttributes?.displayName || childConfig.keyName}
                                                            </span>
                                                        </div>
                                                        {isSelected && (
                                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        }
                                        return null
                                    })}
                                </div>
                                
                                {/* Right Panel: Show selected payment type's children */}
                                <div className="flex-1 flex flex-col">
                                    {selectedPaymentTypeConfig && (
                                        <>
                                            {selectedPaymentTypeConfig.displayAttributes?.displayName && (
                                                <h3 className="text-xl font-semibold mb-2">
                                                    {selectedPaymentTypeConfig.displayAttributes.displayName}
                                                </h3>
                                            )}
                                            {selectedPaymentTypeConfig.children?.map((childConfig, index) => {
                                                return RenderFormFields({
                                                    config: childConfig,
                                                    parent: selectedPaymentTypeConfig,
                                                    childIndex: index,
                                                    sectionId,
                                                    formSubmitClick,
                                                    viewMode,
                                                    rerenderFormUI: localRerenderFormUI || rerenderFormUI
                                                })
                                            })}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )
                }
                
                // Regular ELEMENT_CONDITION_GROUP rendering
                return (
                    <div className="flex flex-col gap-1" id={config.keyName} key={config.keyName}>
                        {displayName ? <p className="text-lg">{displayName}</p> : null}
                        <div className="flex flex-col flex-wrap gap-3">
                            {
                                //ELEMENT_GROUP will always have a children
                                config.children?.map((childConfig, index) => {
                                    return RenderFormFields({
                                        config: childConfig,
                                        parent: config,
                                        childIndex: index,
                                        sectionId,
                                        formSubmitClick,
                                        viewMode,
                                        rerenderFormUI: localRerenderFormUI || rerenderFormUI
                                    })
                                })
                            }
                        </div>
                    </div>
                )

            }
        }
    }

    console.log("form fields inside HOC: ", config)

    return (
        <>
            {
                RenderFormFields({
                    config,
                    parent,
                    childIndex,
                    sectionId,
                    formSubmitClick,
                    viewMode,
                    rerenderFormUI
                })
            }
        </>
    )
}